import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ref, onValue, off } from "firebase/database";

// ─── Config ────────────────────────────────────────────────────────────────
const WAIT_SCROLL_PX_PER_SEC = 30; // scroll speed (original)
const PARK_ORDER = ["MK", "EPCOT", "HS", "AK", "DL", "CA"];

const PARK_THEMES = {
  MK: { bg: "#0B3FA5", text: "#F4C76A" },
  EPCOT: { bg: "#9FA1A4", text: "#ffffff" },
  HS: { bg: "#1fa3a8", text: "#ffffff" },
  AK: { bg: "#5A4636", text: "#6fbf73" },
  DL: { bg: "#d94a7c", text: "#ffffff" },
  CA: { bg: "#F4A300", text: "#E10600" },
};

const PARK_NAME = {
  MK: "Magic Kingdom",
  EPCOT: "EPCOT",
  HS: "Hollywood Studios",
  AK: "Animal Kingdom",
  DL: "Disneyland",
  CA: "California Adventure",
};

const SHADOW_STRONG =
  "0 3px 0 rgba(11,31,53,0.16), 0 24px 48px rgba(11,31,53,0.22), 0 10px 18px rgba(11,31,53,0.10), 0 2px 0 rgba(255,255,255,0.88) inset";

// ─── Wait formatting ───────────────────────────────────────────────────────
// Reads the ride synchronizer's RTDB schema (NOT themeparks.wiki's shape).
// Output strings are the same as the original ("45 min" / "VQ" / "Closed" /
// "Open") so the badge logic downstream is unchanged.
function formatWait(ride) {
  if (!ride) return "\u2014";
  const status = ride.status;

  if (status === "OPERATING") {
    // resp_code 0 = a real standby wait time is present.
    if (ride.resp_code === 0 && typeof ride.wait_time === "number") {
      return `${ride.wait_time} min`;
    }
    // Virtual queue.
    if (ride.resp_msg === "Virtual Queue") return "VQ";
    // Operating but no posted wait.
    return "Open";
  }

  if (status === "CLOSED" || status === "DOWN" || status === "REFURBISHMENT") {
    return "Closed";
  }

  return status || "\u2014";
}

// Numeric suffix of a ride id ("MK_13" -> 13) for true 1..N ordering.
function rideNum(id) {
  const n = parseInt(String(id).split("_")[1], 10);
  return Number.isFinite(n) ? n : 0;
}

// ─── Component ────────────────────────────────────────────────────────────
const WaitTimesScroll = ({ database, subscribedParkKeys, style }) => {
  // Raw ride data per park, straight from RTDB.
  const [parksData, setParksData] = useState({}); // { MK: {MK_1:{...}}, ... }

  // ── Which parks to show ──────────────────────────────────────────────
  const shownParks = useMemo(() => {
    if (Array.isArray(subscribedParkKeys)) {
      return PARK_ORDER.filter((pk) => subscribedParkKeys.includes(pk));
    }
    return PARK_ORDER;
  }, [subscribedParkKeys]);

  // ── Subscribe to the whole waitTimes/parks tree ──────────────────────
  // One listener; wait data is identical for every device so there's no
  // per-device path. Filtering to subscribed parks happens below.
  useEffect(() => {
    if (!database) return;
    const r = ref(database, "waitTimes/parks");
    const handler = onValue(r, (snap) => {
      setParksData(snap.exists() ? snap.val() || {} : {});
    });
    return () => {
      off(r);
      if (typeof handler === "function") handler();
    };
  }, [database]);

  // ── Build the flat ride row list (subscribed parks, in order) ────────
  const rideRows = useMemo(() => {
    const rows = [];
    for (const pk of shownParks) {
      const parkObj = parksData?.[pk];
      if (!parkObj || typeof parkObj !== "object") continue;

      const ids = Object.keys(parkObj).sort((a, b) => rideNum(a) - rideNum(b));
      for (const id of ids) {
        const ride = parkObj[id];
        if (!ride) continue;
        rows.push({
          code: id,
          park: pk,
          name: ride.name || id,
          text: formatWait(ride),
        });
      }
    }
    return rows;
  }, [parksData, shownParks]);

  // Structural signature of the row list: which rides, in what order. This
  // changes ONLY when rides are added/removed (e.g. a park subscription
  // change) — NOT when wait-time numbers update. The scroll engine keys off
  // this so a routine RTDB data refresh (every ~10s) does not restart the
  // scroll. Without this the scroll snaps back to the top every refresh.
  const rideStructureKey = useMemo(
    () => rideRows.map((r) => r.code).join("|"),
    [rideRows]
  );

  // ── Scroll refs (PERF: no React state for scroll position) ───────────
  const waitViewportRef = useRef(null);
  const waitContentRef = useRef(null);
  const waitScrollPosRef = useRef(0);
  const waitRafRef = useRef(0);
  const waitLastTRef = useRef(0);

  const rideRowRefs = useRef([]);
  const rideRowTopsRef = useRef([]); // latest measured row tops (ref, not state)
  const rideRowsRef = useRef(rideRows); // latest rows, read by the rAF loop
  const [activePark, setActivePark] = useState("MK");
  const activeParkRef = useRef("MK");

  // Keep the rows ref current every render so the rAF loop always reads the
  // freshest data WITHOUT the scroll effect needing to restart.
  rideRowsRef.current = rideRows;

  // Keep activePark valid if the subscribed-park set changes.
  useEffect(() => {
    if (shownParks.length && !shownParks.includes(activeParkRef.current)) {
      activeParkRef.current = shownParks[0];
      setActivePark(shownParks[0]);
    }
  }, [shownParks]);

  // ── Measure row top offsets for theme detection ──────────────────────
  // Re-measures only when the row STRUCTURE changes (rides added/removed),
  // not on every wait-time refresh. Writes to a ref so it never triggers a
  // scroll-effect restart.
  useLayoutEffect(() => {
    const measure = () => {
      const content = waitContentRef.current;
      if (!content) return;
      const tops = rideRowRefs.current.map((el) => (el ? el.offsetTop || 0 : 0));
      rideRowTopsRef.current = tops;
    };

    measure();
    const raf = requestAnimationFrame(measure);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && waitContentRef.current) {
      ro = new ResizeObserver(() => measure());
      ro.observe(waitContentRef.current);
    }

    return () => {
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
    };
  }, [rideStructureKey]);

  // ── Continuous rAF scroll + throttled theme detection ────────────────
  // The scroll engine itself is the ORIGINAL commandconsole code — tuned
  // for smooth playback on slow Android displays (direct translate3d,
  // clamped dt, theme check throttled to ~250ms). Do not refactor to state.
  //
  // The KEY change vs the original: this effect depends on rideStructureKey,
  // NOT on the rideRows array. So a routine RTDB wait-time refresh (every
  // ~10s) does NOT tear down and restart the scroll — position is preserved.
  // The loop reads live rows/tops via refs. The effect only restarts when
  // the actual set of rides changes (a park subscription change), which is
  // correct — that genuinely needs a fresh scroll.
  //
  // TIMING: this is the prototype's original delta-accumulation scroll
  // (per-frame dy, dt clamped at 50ms) — kept verbatim, as it was tuned for
  // smooth playback on slow Android displays.
  useEffect(() => {
    cancelAnimationFrame(waitRafRef.current);

    waitScrollPosRef.current = 0;
    waitLastTRef.current = 0;

    let lastThemeCheck = 0;

    const step = (t) => {
      const vp = waitViewportRef.current;
      const content = waitContentRef.current;
      if (!vp || !content) {
        waitRafRef.current = requestAnimationFrame(step);
        return;
      }

      const vpH = vp.clientHeight;
      const contentH = content.scrollHeight;

      if (contentH <= vpH + 4) {
        waitScrollPosRef.current = 0;
        content.style.transform = "translate3d(0,0,0)";
        waitRafRef.current = requestAnimationFrame(step);
        return;
      }

      const lastT = waitLastTRef.current || t;
      const dtMs = Math.min(50, Math.max(0, t - lastT)); // clamp dt
      waitLastTRef.current = t;

      const dy = (WAIT_SCROLL_PX_PER_SEC * dtMs) / 1000;
      let y = waitScrollPosRef.current + dy;

      const maxY = contentH - vpH;
      if (y > maxY) y = 0;

      waitScrollPosRef.current = y;
      content.style.transform = `translate3d(0, ${-y}px, 0)`;

      // theme detection (every ~250ms) — reads live rows/tops via refs.
      const tops = rideRowTopsRef.current;
      const rows = rideRowsRef.current;
      if (t - lastThemeCheck > 250 && tops.length && rows.length) {
        lastThemeCheck = t;

        const anchor = y + Math.max(10, Math.floor(vpH * 0.15));
        let idxRow = 0;
        while (idxRow + 1 < tops.length && anchor >= tops[idxRow + 1]) idxRow++;

        const row = rows[idxRow];
        const nextPark = row?.park || "MK";
        if (nextPark !== activeParkRef.current) {
          activeParkRef.current = nextPark;
          setActivePark(nextPark);
        }
      }

      waitRafRef.current = requestAnimationFrame(step);
    };

    waitRafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(waitRafRef.current);
  }, [rideStructureKey]);

  // ── Theme + styles ───────────────────────────────────────────────────
  const theme = PARK_THEMES[activePark] || PARK_THEMES.MK;
  const activeParkName = PARK_NAME[activePark] || activePark;

  const s = {
    ridesCard: {
      borderRadius: 22,
      border: "1px solid rgba(255,255,255,0.16)",
      background: `linear-gradient(180deg, ${theme.bg} 0%, rgba(0,0,0,0.06) 140%), linear-gradient(180deg, ${theme.bg} 0%, ${theme.bg} 100%)`,
      boxShadow: SHADOW_STRONG,
      padding: 16,
      color: theme.text,
      // Explicit Lato — without this the text inherits the wrong font
      // (the LED dot font injected by the shows board leaks in otherwise).
      fontFamily: "'Lato', system-ui, -apple-system, sans-serif",
      outline: "1px solid rgba(255,255,255,0.20)",
      outlineOffset: "-2px",
      minHeight: 0,
      minWidth: 0,
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      position: "relative",
      overflow: "hidden",
      // smooth crossfade as the active park changes
      transition: "background 0.5s ease, color 0.5s ease",
    },
    ridesSheen: {
      position: "absolute",
      inset: 0,
      background:
        "radial-gradient(900px 240px at 30% -20%, rgba(255,255,255,0.25), transparent 60%), radial-gradient(800px 260px at 110% 30%, rgba(255,255,255,0.16), transparent 55%)",
      pointerEvents: "none",
      opacity: 0.9,
    },
    ridesTitle: {
      fontWeight: 950,
      fontSize: 16,
      color: "rgba(255,255,255,0.86)",
      letterSpacing: 0.28,
      marginBottom: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      flex: "0 0 auto",
      position: "relative",
      zIndex: 1,
      minWidth: 0,
    },
    ridesPill: {
      fontSize: 14,
      fontWeight: 950,
      color: "rgba(255,255,255,0.92)",
      background: "rgba(255,255,255,0.12)",
      border: "1px solid rgba(255,255,255,0.18)",
      padding: "6px 9px",
      borderRadius: 999,
      letterSpacing: 0.25,
      whiteSpace: "nowrap",
      flex: "0 0 auto",
    },
    waitViewport: {
      position: "relative",
      flex: "1 1 auto",
      overflow: "hidden",
      borderRadius: 16,
      border: "1px solid rgba(255,255,255,0.18)",
      background: "rgba(255,255,255,0.10)",
      zIndex: 1,
      minWidth: 0,
    },
    waitContent: {
      position: "absolute",
      left: 0,
      right: 0,
      top: 0,
      transform: "translate3d(0,0,0)",
      willChange: "transform",
      padding: "6px 12px",
    },
    waitRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12px 0",
      borderTop: "1px solid rgba(255,255,255,0.18)",
      gap: 12,
      minWidth: 0,
    },
    waitName: {
      flex: "1 1 auto",
      minWidth: 0,
      color: theme.text,
      fontSize: 18,
      fontWeight: 950,
      letterSpacing: -0.15,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    waitMins: {
      flex: "0 0 auto",
      whiteSpace: "nowrap",
      color: theme.text,
      fontWeight: 975,
      fontSize: 18,
    },
    waitBadge: (kind) => ({
      fontSize: 16,
      fontWeight: 950,
      padding: "6px 9px",
      borderRadius: 999,
      border: "1px solid rgba(255,255,255,0.20)",
      background:
        kind === "closed"
          ? "rgba(239,68,68,0.18)"
          : kind === "vq"
          ? "rgba(245,158,11,0.18)"
          : "rgba(255,255,255,0.12)",
      color: "rgba(255,255,255,0.95)",
      whiteSpace: "nowrap",
    }),
  };

  return (
    <div style={{ ...s.ridesCard, ...style }}>
      <div style={s.ridesSheen} />
      <div style={s.ridesTitle}>
        <span>Current Wait Times</span>
        <span style={s.ridesPill}>{activeParkName}</span>
      </div>

      <div ref={waitViewportRef} style={s.waitViewport}>
        <div ref={waitContentRef} style={s.waitContent}>
          {rideRows.map((r, i) => {
            const label = r.text;
            const isClosed = label === "Closed";
            const isVQ = label === "VQ";
            const badgeKind = isClosed ? "closed" : isVQ ? "vq" : "open";

            return (
              <div
                key={r.code}
                ref={(node) => {
                  rideRowRefs.current[i] = node;
                }}
                style={{
                  ...s.waitRow,
                  borderTop: i === 0 ? "none" : s.waitRow.borderTop,
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <span style={s.waitName} title={r.name}>
                    {r.name}
                  </span>
                </div>

                <span style={badgeKind !== "open" ? s.waitBadge(badgeKind) : s.waitMins}>
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WaitTimesScroll;
